// One controller per mounted page. No browser-persisted insight content.
export function normalizeSelection({page, dataset, from, to, filters = {}}) {
  const day = value => value instanceof Date
    ? [value.getFullYear(), String(value.getMonth()+1).padStart(2,'0'), String(value.getDate()).padStart(2,'0')].join('-')
    : value;
  return {page, ...(dataset ? {dataset} : {}), from: day(from), to: day(to),
    filters: Object.fromEntries(Object.entries(filters).sort(([a],[b])=>a.localeCompare(b))
      .map(([key, values])=>[key,[...new Set(values)].sort()]).filter(([,values])=>values.length))};
}

export function createInsightController({api, onChange, schedule=setTimeout, unschedule=clearTimeout, now=Date.now, nonce=()=>crypto.randomUUID()}) {
  let disposed=false, version=0, timer, abort, current, key='', state={status:'waiting'}, started=0;
  const emit=next=>{state={...state,...next};if(!disposed)onChange(state);};
  const clear=()=>{unschedule(timer);abort?.abort();};
  const active=v=>!disposed&&v===version;
  const failure=(error,v)=>{if(active(v)&&error.name!=='AbortError')emit({status:error.status===429?'limited':'failed',error:error.message});};
  async function accept(data,v) {
    if(!active(v))return;
    if(['queued','running'].includes(data.status)) {
      const created=Date.parse(data.created_at);
      if(Number.isFinite(created))started=Math.min(started,created);
      emit({status:data.status,jobId:data.id,canCancel:data.can_cancel===true,error:null});
      if(now()-started>=600000){emit({status:'failed',error:'Insights took too long. Retry explicitly.'});return;}
      timer=schedule(async()=>{
        try {await accept(await api.get('/dashboard/insights/'+data.id,{signal:abort.signal}),v);}
        catch(error){failure(error,v);}
      },now()-started<10000?2000:5000);
    } else {
      emit({status:data.status,jobId:data.id,error:data.error||null,
        result:data.status==='succeeded'?data:null,outdated:false});
    }
  }
  async function generate(v,regenerate) {
    if(!active(v))return;
    started=now();abort=new AbortController();
    emit({status:'submitting',resumed:false,error:null});
    try {
      await accept(await api.post('/dashboard/insights',{...current,...(regenerate?{regenerate}: {})},{signal:abort.signal}),v);
    } catch(error){failure(error,v);}
  }
  async function load(v) {
    if(!active(v))return;
    started=now();abort=new AbortController();
    emit({status:'checking',error:null});
    try {
      const data=await api.post('/dashboard/insights/lookup',current,{signal:abort.signal});
      if(!active(v))return;
      if(data.status==='missing')await generate(v);
      else {emit({resumed:true});await accept(data,v);}
    } catch(error){failure(error,v);}
  }
  return {
    update(input) {
      const next=normalizeSelection(input), nextKey=JSON.stringify(next);
      if(nextKey===key)return;
      clear();current=next;key=nextKey;const v=++version;
      emit({status:'waiting',selection:next,outdated:!!state.result,error:null,jobId:null,resumed:false,canCancel:false});
      if(next.from&&next.to)timer=schedule(()=>load(v),1500);
    },
    regenerate() {if(!current||disposed)return;clear();const v=++version;emit({outdated:!!state.result});generate(v,nonce());},
    async cancel() {
      if(!state.jobId||!state.canCancel)return;
      const job=state.jobId;clear();const v=++version;
      try {await api.del('/dashboard/insights/'+job);if(active(v))emit({status:'cancelled',error:'Generation cancelled. Regenerate when ready.'});}
      catch(error){failure(error,v);}
    },
    dispose(){disposed=true;++version;clear();},
  };
}
